import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import ClientTabs from "./ClientTabs";
import { useActiveClients } from "../../hooks/useActiveClients";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { aud, fmtD, AccountStatusPill, VariationDetail, AccountTotals, RowExpander } from "../../components/ClientAccount";
import { BlockerToggle, BlockerBadge } from "../../components/BlockerControl";
import { BusinessDetailsModal, GenerateDocDialog, BillingDocsHistory } from "../../components/BillingDocs";
import { toast } from "sonner";
import { Plus, Trash2, Clock, ChevronDown, Pencil, FileText, Settings } from "lucide-react";

const fieldCls = "rounded-lg px-2.5 py-1.5 outline-none border focus:ring-2 focus:ring-[#F97316] text-xs";
const border = { borderColor: "#E5E7EB" };

function PricingFields({ kind, reason, setReason, hours, setHours, rate, setRate, amount, setAmount }) {
  if (kind === "variation") {
    return (
      <>
        <div className="min-w-[160px]">
          <label className="text-xs text-[#6B7280] mb-1 block">Reason</label>
          <input data-testid="account-reason-input" value={reason} onChange={(e) => setReason(e.target.value)} className={`${fieldCls} w-full`} style={border} />
        </div>
        <div className="w-20">
          <label className="text-xs text-[#6B7280] mb-1 block">Hours</label>
          <input data-testid="account-hours-input" value={hours} onChange={(e) => setHours(e.target.value)} className={`${fieldCls} w-full`} style={border} />
        </div>
        <div className="w-24">
          <label className="text-xs text-[#6B7280] mb-1 block">Rate ($/hr)</label>
          <input data-testid="account-rate-input" value={rate} onChange={(e) => setRate(e.target.value)} className={`${fieldCls} w-full`} style={border} />
        </div>
      </>
    );
  }
  return (
    <div className="w-28">
      <label className="text-xs text-[#6B7280] mb-1 block">Amount ($)</label>
      <input data-testid="account-amount-input" value={amount} onChange={(e) => setAmount(e.target.value)} className={`${fieldCls} w-full`} style={border} />
    </div>
  );
}

function ItemTypeFields({ item, setItem, kind, setKind }) {
  return (
    <>
      <div className="flex-1 min-w-[180px]">
        <label className="text-xs text-[#6B7280] mb-1 block">Item</label>
        <input data-testid="account-item-input" value={item} onChange={(e) => setItem(e.target.value)} className={`${fieldCls} w-full`} style={border} placeholder="e.g. Phase 2 — Procedures build" />
      </div>
      <div>
        <label className="text-xs text-[#6B7280] mb-1 block">Type</label>
        <select data-testid="account-kind-select" value={kind} onChange={(e) => setKind(e.target.value)} className={fieldCls} style={border}>
          <option value="phase">Phase</option>
          <option value="variation">Variation</option>
        </select>
      </div>
    </>
  );
}

function ProposedWorkField({ proposedWork, setProposedWork }) {
  return (
    <>
      <div className="w-full">
        <label className="text-xs text-[#6B7280] mb-1 block">Proposed work</label>
        <textarea data-testid="account-proposed-work-input" value={proposedWork} onChange={(e) => setProposedWork(e.target.value)} rows={2}
          className={`${fieldCls} w-full resize-y`} style={border} placeholder="Describe the proposed work the client will see under this item..." />
      </div>
      <p className="w-full text-[11px] text-[#9CA3AF] mt-0.5">
        Leave amount/hours blank to log a request with no quote yet. An amount (or hours × rate) creates the row at Quoted.
        Only the client can approve a quoted row. Variations are invoiced separately from phases; multiple variations may share one invoice number.
      </p>
    </>
  );
}

function AddItemForm({ clientId, onSaved }) {
  const [item, setItem] = useState("");
  const [kind, setKind] = useState("phase");
  const [reason, setReason] = useState("");
  const [proposedWork, setProposedWork] = useState("");
  const [hours, setHours] = useState("");
  const [rate, setRate] = useState("");
  const [amount, setAmount] = useState("");

  const save = async () => {
    if (!item.trim()) { toast.error("Item name required"); return; }
    await api.post(`/clients/${clientId}/account`, {
      item, kind, reason,
      proposed_work: proposedWork,
      hours: hours ? parseFloat(hours) : null,
      rate: rate ? parseFloat(rate) : null,
      amount: amount ? parseFloat(amount) : null,
    });
    toast.success("Item added to billing");
    setItem(""); setReason(""); setProposedWork(""); setHours(""); setRate(""); setAmount("");
    onSaved();
  };

  return (
    <div className="flex flex-wrap items-end gap-2 p-4 rounded-xl border mb-4 bg-white" style={border} data-testid="account-add-form">
      <ItemTypeFields item={item} setItem={setItem} kind={kind} setKind={setKind} />
      <PricingFields kind={kind} reason={reason} setReason={setReason} hours={hours} setHours={setHours}
        rate={rate} setRate={setRate} amount={amount} setAmount={setAmount} />
      <button data-testid="account-add-save" onClick={save} className="rounded-lg px-4 py-2 font-semibold text-white text-xs" style={{ backgroundColor: "#0A1F44" }}>
        Add Item
      </button>
      <ProposedWorkField proposedWork={proposedWork} setProposedWork={setProposedWork} />
    </div>
  );
}

async function putRow(clientId, rowId, body, msg, reload) {
  try {
    await api.put(`/clients/${clientId}/account/${rowId}`, body);
    toast.success(msg);
    reload();
  } catch (e) {
    toast.error(e.response?.data?.detail || "Update failed");
  }
}

function RequestedActions({ clientId, r, reload }) {
  const [hours, setHours] = useState(r.hours ?? "");
  const [rate, setRate] = useState(r.rate ?? "");

  const quote = () => {
    const h = parseFloat(hours), rt = parseFloat(rate);
    if (!h || !rt) { toast.error("Hours and rate required to quote"); return; }
    putRow(clientId, r.id, { hours: h, rate: rt, status: "Quoted" }, "Quoted — client can now approve", reload);
  };

  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      <button data-testid={`account-include-${r.id}`}
        onClick={() => putRow(clientId, r.id, { status: "Included" }, "Marked as included — in scope", reload)}
        className="text-xs font-semibold px-2.5 py-1.5 rounded-lg border text-[#6B7280]" style={border}>In scope · Include</button>
      <input data-testid={`account-quote-hours-${r.id}`} value={hours} onChange={(e) => setHours(e.target.value)} placeholder="Hrs" className={`${fieldCls} w-14`} style={border} />
      <input data-testid={`account-quote-rate-${r.id}`} value={rate} onChange={(e) => setRate(e.target.value)} placeholder="Rate" className={`${fieldCls} w-16`} style={border} />
      <button data-testid={`account-quote-${r.id}`} onClick={quote}
        className="text-xs font-semibold px-2.5 py-1.5 rounded-lg text-white" style={{ backgroundColor: "#F97316" }}>Quote</button>
    </div>
  );
}

function ApprovedActions({ clientId, r, reload }) {
  const [actual, setActual] = useState(r.actual_hours ?? "");
  const [inv, setInv] = useState(r.invoice_number || "");
  const [issued, setIssued] = useState(r.invoice_issued || "");

  const logActual = () => {
    const a = parseFloat(actual);
    if (!a) { toast.error("Enter actual hours"); return; }
    putRow(clientId, r.id, { actual_hours: a }, "Actual hours logged", reload);
  };

  const invoice = () => {
    if (!inv.trim() || !issued) { toast.error("Invoice number and issue date required"); return; }
    putRow(clientId, r.id, { invoice_number: inv.trim(), invoice_issued: issued },
      "Invoiced — due date set to issue + 14 days", reload);
  };

  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      <input data-testid={`account-actual-${r.id}`} value={actual} onChange={(e) => setActual(e.target.value)} placeholder="Actual hrs" className={`${fieldCls} w-20`} style={border} />
      <button data-testid={`account-actual-save-${r.id}`} onClick={logActual}
        className="text-xs font-semibold px-2 py-1.5 rounded-lg border text-[#6B7280]" style={border}>Log</button>
      <input data-testid={`account-invoice-num-${r.id}`} value={inv} onChange={(e) => setInv(e.target.value)} placeholder="Invoice #" className={`${fieldCls} w-20`} style={border} />
      <input data-testid={`account-invoice-date-${r.id}`} type="date" value={issued} onChange={(e) => setIssued(e.target.value)} className={`${fieldCls}`} style={border} />
      <button data-testid={`account-invoice-${r.id}`} onClick={invoice}
        className="text-xs font-semibold px-2.5 py-1.5 rounded-lg text-white" style={{ backgroundColor: "#0A1F44" }}>Invoice</button>
    </div>
  );
}

function InvoicedActions({ clientId, r, reload }) {
  const [paid, setPaid] = useState(r.paid_date || "");

  const markPaid = () => {
    if (!paid) { toast.error("Select the paid date"); return; }
    putRow(clientId, r.id, { paid_date: paid }, "Marked paid", reload);
  };

  return (
    <div className="flex items-center gap-1.5">
      <input data-testid={`account-paid-date-${r.id}`} type="date" value={paid} onChange={(e) => setPaid(e.target.value)} className={fieldCls} style={border} />
      <button data-testid={`account-paid-${r.id}`} onClick={markPaid}
        className="text-xs font-semibold px-2.5 py-1.5 rounded-lg text-white" style={{ backgroundColor: "#10B981" }}>Mark paid</button>
    </div>
  );
}

function validateInvoiceEdit(r, inv, issued, paid) {
  if (!inv.trim() || !issued) return "Invoice number and issue date required";
  if (r.status === "Paid" && !paid) return "Paid date required";
  return null;
}

function InvoiceEditFields({ r, inv, setInv, issued, setIssued, paid, setPaid, onSave }) {
  return (
    <>
      <input data-testid={`edit-invoice-num-${r.id}`} value={inv} onChange={(e) => setInv(e.target.value)} placeholder="Invoice #" className={`${fieldCls} w-24`} style={border} />
      <input data-testid={`edit-invoice-date-${r.id}`} type="date" value={issued} onChange={(e) => setIssued(e.target.value)} title="Issue date (due = issue + 14 days)" className={fieldCls} style={border} />
      {r.status === "Paid" && (
        <input data-testid={`edit-paid-date-${r.id}`} type="date" value={paid} onChange={(e) => setPaid(e.target.value)} title="Paid date" className={fieldCls} style={border} />
      )}
      <button data-testid={`edit-invoice-save-${r.id}`} onClick={onSave}
        className="text-xs font-semibold px-2.5 py-1.5 rounded-lg text-white" style={{ backgroundColor: "#0A1F44" }}>Save</button>
    </>
  );
}

function EditInvoiceDetails({ clientId, r, reload }) {
  const [open, setOpen] = useState(false);
  const [inv, setInv] = useState(r.invoice_number || "");
  const [issued, setIssued] = useState(r.invoice_issued || "");
  const [paid, setPaid] = useState(r.paid_date || "");

  const save = () => {
    const error = validateInvoiceEdit(r, inv, issued, paid);
    if (error) { toast.error(error); return; }
    const body = { invoice_number: inv.trim(), invoice_issued: issued };
    if (r.status === "Paid") body.paid_date = paid;
    putRow(clientId, r.id, body, "Invoice details updated — due date recalculated", reload);
    setOpen(false);
  };

  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      <button data-testid={`edit-invoice-${r.id}`} onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-lg border text-[#6B7280] hover:text-[#F97316] hover:border-[#F97316]" style={border}>
        <Pencil size={12} /> {open ? "Cancel edit" : "Edit invoice"}
      </button>
      {open && (
        <InvoiceEditFields r={r} inv={inv} setInv={setInv} issued={issued} setIssued={setIssued}
          paid={paid} setPaid={setPaid} onSave={save} />
      )}
    </div>
  );
}

function RowActions({ clientId, r, reload }) {
  if (r.status === "Requested") return <RequestedActions clientId={clientId} r={r} reload={reload} />;
  if (r.status === "Quoted") return <p className="text-xs text-[#6B7280]">Waiting on client approval.</p>;
  if (r.status === "Approved") return <ApprovedActions clientId={clientId} r={r} reload={reload} />;
  if (r.status === "Invoiced") {
    return (
      <div className="flex items-center gap-3 flex-wrap">
        <InvoicedActions clientId={clientId} r={r} reload={reload} />
        <EditInvoiceDetails clientId={clientId} r={r} reload={reload} />
      </div>
    );
  }
  if (r.status === "Paid") return <EditInvoiceDetails clientId={clientId} r={r} reload={reload} />;
  return null;
}

function ProposedWork({ clientId, r, reload }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(r.proposed_work || "");

  const save = async () => {
    try {
      await api.put(`/clients/${clientId}/account/${r.id}`, { proposed_work: text });
      toast.success("Proposed work saved");
      setOpen(false);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    }
  };

  return (
    <div className="mt-1">
      <button data-testid={`proposed-work-toggle-${r.id}`} onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1 text-xs font-semibold" style={{ color: "#F97316" }}>
        <ChevronDown size={12} className={`transition-transform ${open ? "rotate-180" : ""}`} />
        Proposed work{r.proposed_work ? "" : " (empty)"}
      </button>
      {open && (
        <div className="mt-1">
          <textarea data-testid={`proposed-work-input-${r.id}`} value={text} onChange={(e) => setText(e.target.value)} rows={2}
            className={`${fieldCls} w-full resize-y`} style={border}
            placeholder="Describe the proposed work for this item..." />
          <button data-testid={`proposed-work-save-${r.id}`} onClick={save}
            className="text-xs font-semibold px-2.5 py-1 rounded-lg text-white mt-1" style={{ backgroundColor: "#0A1F44" }}>Save</button>
        </div>
      )}
    </div>
  );
}

function RowBadges({ r }) {
  return (
    <>
      {r.blocker && <BlockerBadge note={r.blocker_note} testId={`admin-blocker-badge-${r.id}`} />}
      {r.feedback_id && <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded" style={{ backgroundColor: "#FEF0E6", color: "#F97316" }}>from feedback</span>}
      {r.decision_response_id && <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded" style={{ backgroundColor: "#FEF0E6", color: "#F97316" }}>from decision</span>}
      {r.status === "Quoted" && r.quote_age_days > 7 && (
        <span data-testid={`account-quote-age-${r.id}`} className="text-[10px] font-bold px-1.5 py-0.5 rounded flex items-center gap-1" style={{ backgroundColor: "#FEE2E2", color: "#B91C1C" }}>
          <Clock size={10} /> quoted {r.quote_age_days}d ago
        </span>
      )}
    </>
  );
}

function RowItemCell({ clientId, r, muted, reload }) {
  return (
    <div className="col-span-4">
      <p className="text-sm font-medium flex items-center gap-2 flex-wrap">
        {r.item}
        <RowBadges r={r} />
      </p>
      <VariationDetail r={r} muted={muted} />
      {r.feedback_text && <RowExpander label="View feedback" testId={`view-feedback-${r.id}`}>{r.feedback_text}</RowExpander>}
      <ProposedWork clientId={clientId} r={r} reload={reload} />
      {r.approved_by && <p className="text-xs mt-0.5" style={{ color: "#10B981" }}>Approved by {r.approved_by}, {fmtD(r.approved_at)}</p>}
      {r.declined_by && <p className="text-xs mt-0.5 text-[#9CA3AF]">Declined by {r.declined_by}, {fmtD(r.declined_at)}</p>}
    </div>
  );
}

function AccountRow({ clientId, r, reload, selected, onToggle }) {
  const [confirmDel, setConfirmDel] = useState(false);
  const muted = r.status === "Included" || r.status === "Declined";
  return (
    <div data-testid={`admin-account-row-${r.id}`}
      className={`px-4 py-3 border-b last:border-b-0 ${muted && !selected ? "opacity-50 bg-gray-50" : ""}`}
      style={{ borderColor: "#F3F4F6", backgroundColor: selected ? "#FFF7F0" : undefined }}>
      <div className="flex items-start gap-3">
        <input type="checkbox" data-testid={`billing-select-${r.id}`} checked={selected} onChange={onToggle}
          className="mt-1 h-4 w-4 accent-[#F97316] cursor-pointer shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="grid grid-cols-12 gap-2 items-center">
            <RowItemCell clientId={clientId} r={r} muted={muted} reload={reload} />
            <div className="col-span-1 text-sm">{aud(r.amount)}</div>
            <div className="col-span-1 text-xs text-[#6B7280]">{r.invoice_number || "—"}</div>
            <div className="col-span-1 text-xs text-[#6B7280]">{fmtD(r.invoice_issued)}</div>
            <div className="col-span-1 text-xs text-[#6B7280]">{fmtD(r.due_date)}</div>
            <div className="col-span-1 text-xs text-[#6B7280]">{fmtD(r.paid_date)}</div>
            <div className="col-span-2"><AccountStatusPill status={r.status} /></div>
            <div className="col-span-1 flex justify-end items-center gap-1">
              <BlockerToggle flagged={r.blocker} note={r.blocker_note} testPrefix={`account-blocker-${r.id}`}
                onSave={(flag, note) => putRow(clientId, r.id, { blocker: flag, blocker_note: note },
                  flag ? "Blocker flagged — the client will see a warning on this billing item" : "Blocker removed", reload)} />
              <button data-testid={`account-delete-${r.id}`} onClick={() => setConfirmDel(true)} className="p-1.5 rounded hover:bg-gray-100 text-red-400"><Trash2 size={14} /></button>
            </div>
          </div>
          <div className="mt-2">
            <RowActions clientId={clientId} r={r} reload={reload} />
          </div>
        </div>
      </div>
      <ConfirmDialog open={confirmDel} message="Remove this row from billing?" confirmLabel="Remove"
        onConfirm={async () => { setConfirmDel(false); await api.delete(`/clients/${clientId}/account/${r.id}`); toast.success("Row removed"); reload(); }}
        onCancel={() => setConfirmDel(false)} testId={`account-confirm-${r.id}`} />
    </div>
  );
}

function LockedNote() {
  return (
    <div data-testid="account-locked-note" className="rounded-lg border px-4 py-2.5 mb-4 text-sm" style={{ backgroundColor: "#F3F4F6", borderColor: "#E5E7EB", color: "#6B7280" }}>
      This feature is <span className="font-semibold">locked</span> for this client — they cannot see any of it.
      Build it up here, then unlock it from the Clients tab when ready.
    </div>
  );
}

function BillingTable({ data, clientId, reload, selected, onToggle }) {
  return (
    <div className="rounded-xl border overflow-x-auto bg-white" style={{ borderColor: "#E5E7EB" }}>
      <div className="grid grid-cols-12 gap-2 py-3 pr-4 text-xs font-semibold uppercase tracking-wider min-w-[760px]" style={{ backgroundColor: "#0A1F44", color: "#fff", paddingLeft: "44px" }}>
        <div className="col-span-4">Item</div>
        <div className="col-span-1">Amount</div>
        <div className="col-span-1">Invoice</div>
        <div className="col-span-1">Issued</div>
        <div className="col-span-1">Due</div>
        <div className="col-span-1">Paid</div>
        <div className="col-span-2">Status</div>
        <div className="col-span-1"></div>
      </div>
      {data && data.rows.length === 0 && <p className="text-center py-10 text-[#6B7280] text-sm">Nothing on billing yet. Add the proposal phases above.</p>}
      {data && data.rows.map((r) => (
        <AccountRow key={r.id} clientId={clientId} r={r} reload={reload}
          selected={selected.includes(r.id)} onToggle={() => onToggle(r.id)} />
      ))}
    </div>
  );
}

function BillingDocToolbar({ selected, onSelectAllUnpaid, onClear, onGenerate, onBusiness }) {
  return (
    <div className="mb-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <button data-testid="business-details-btn" onClick={onBusiness}
          className="flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg border text-[#6B7280] hover:border-[#F97316] hover:text-[#F97316]" style={border}>
          <Settings size={13} /> Business details
        </button>
        <div className="flex items-center gap-2 flex-wrap">
          <button data-testid="billing-select-all-unpaid" onClick={onSelectAllUnpaid}
            className="text-xs font-semibold px-3 py-2 rounded-lg border text-[#6B7280] hover:border-[#0A1F44] hover:text-[#0A1F44]" style={border}>
            Select all unpaid
          </button>
          {selected.length > 0 && (
            <button data-testid="billing-clear-selection" onClick={onClear}
              className="text-xs font-semibold px-3 py-2 rounded-lg border text-[#9CA3AF]" style={border}>Clear</button>
          )}
          <button data-testid="billing-generate-btn" disabled={!selected.length} onClick={onGenerate}
            className="flex items-center gap-1.5 text-xs font-bold px-4 py-2 rounded-lg text-white disabled:opacity-40" style={{ backgroundColor: "#F97316" }}>
            <FileText size={13} /> Generate document ({selected.length} selected)
          </button>
        </div>
      </div>
      <p className="text-[11px] text-[#9CA3AF] mt-1.5 text-right">Tick items below — selected items appear on the PDF statement or invoice.</p>
    </div>
  );
}

export default function AdminAccount() {
  const { clients, active, setActive, status: clientsStatus, error: clientsError, retry: retryClients } = useActiveClients();
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState([]);
  const [genOpen, setGenOpen] = useState(false);
  const [bizOpen, setBizOpen] = useState(false);
  const [docsKey, setDocsKey] = useState(0);

  const load = () => { if (active) api.get(`/clients/${active}/account`).then(({ data }) => setData(data)); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); setSelected([]); }, [active]);

  const toggleSelect = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const selectAllUnpaid = () => {
    const ids = (data?.rows || []).filter((r) => r.status !== "Paid" && r.status !== "Declined").map((r) => r.id);
    setSelected(ids);
    if (!ids.length) toast.info("No unpaid items to select");
  };

  const activeClient = clients.find((c) => c.id === active);
  const locked = activeClient && !activeClient.features_unlocked?.account;

  return (
    <div>
      <h1 className="font-heading text-2xl font-bold mb-1">Billing</h1>
      <p className="text-[#6B7280] text-sm mb-6">One table holding every dollar in the engagement — quoted phases and variations.</p>

      <ClientTabs clients={clients} active={active} onChange={setActive} status={clientsStatus} error={clientsError} onRetry={retryClients} />

      {active && (
        <>
          {locked && <LockedNote />}
          <AddItemForm clientId={active} onSaved={load} />
          <BillingDocToolbar selected={selected} onSelectAllUnpaid={selectAllUnpaid}
            onClear={() => setSelected([])} onGenerate={() => setGenOpen(true)} onBusiness={() => setBizOpen(true)} />
          <BillingTable data={data} clientId={active} reload={load} selected={selected} onToggle={toggleSelect} />
          {data && <AccountTotals totals={data.totals} />}
          <BillingDocsHistory clientId={active} refreshKey={docsKey} />
          <GenerateDocDialog open={genOpen} onClose={() => setGenOpen(false)} clientId={active}
            rows={data?.rows || []} selectedIds={selected}
            onDone={() => { load(); setSelected([]); setDocsKey((k) => k + 1); }} />
          <BusinessDetailsModal open={bizOpen} onClose={() => setBizOpen(false)} />
        </>
      )}
    </div>
  );
}
